import React, { useState } from 'react';
import { Mail, Gift, Sparkles, CheckCircle2, Heart, BellRing } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

const benefits = [
  { icon: Gift, title: 'Exklusive Rabatte', text: '10% Willkommens-Rabatt + regelmäßige Angebote nur für Abonnenten' },
  { icon: Sparkles, title: 'Neue Schätze entdecken', text: 'Erfahren Sie als Erste:r von neuen handgemachten Produkten' },
  { icon: BellRing, title: 'Saisonale Inspiration', text: 'Ideen für jede Jahreszeit - Frühling, Sommer, Herbst & Weihnachten' },
  { icon: Heart, title: 'Von Herzen gemacht', text: 'Hinter den Kulissen, Geschichten und persönliche Empfehlungen' },
];

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error('Bitte stimmen Sie der Datenschutzerklärung zu');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post('/newsletter/subscribe', {
        email,
        name: firstName,
        consent_given: true,
      });
      setSuccess(true);
      toast.success('Fast geschafft! Bitte bestätigen Sie Ihre Anmeldung per E-Mail.');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#E8E0D5' }} data-testid="newsletter-success-page">
        <Card className="max-w-md w-full rounded-none border-2" style={{ borderColor: '#C4A084' }}>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4" style={{ color: '#C4A084' }} />
            <h1 className="text-2xl font-semibold mb-3" style={{ color: '#6D5A47' }}>
              Fast geschafft!
            </h1>
            <p className="text-gray-700 mb-2">
              Wir haben Ihnen eine Bestätigungs-E-Mail an <strong>{email}</strong> gesendet.
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Bitte klicken Sie auf den Link in der E-Mail, um Ihre Anmeldung zu bestätigen und Ihren 10% Willkommens-Rabatt zu erhalten.
            </p>
            <Button
              onClick={() => window.location.href = '/'}
              className="rounded-none w-full text-white"
              style={{ backgroundColor: '#8B7355' }}
              data-testid="back-to-shop-btn"
            >
              Zurück zum Shop
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E8E0D5' }} data-testid="newsletter-signup-page">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #C4A084 0%, #B89968 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative max-w-4xl mx-auto px-6 py-16 sm:py-24 text-center text-white">
          <Mail className="h-12 w-12 mx-auto mb-4 opacity-90" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4" style={{ letterSpacing: '0.05em' }}>
            Bleiben Sie verbunden
          </h1>
          <p className="text-base sm:text-lg max-w-2xl mx-auto opacity-95" style={{ lineHeight: 1.7 }}>
            Werden Sie Teil der Homeyoursoul-Familie - mit handgemachten Schätzen, exklusiven Vorteilen und Inspirationen, die Ihrem Zuhause Seele verleihen.
          </p>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-center text-sm uppercase tracking-widest mb-8" style={{ color: '#8B7355' }}>
          Was Sie erwartet
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="bg-white p-6 border" style={{ borderColor: '#D4B595' }} data-testid={`benefit-${i}`}>
                <Icon className="h-8 w-8 mb-3" style={{ color: '#C4A084' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#6D5A47' }}>{b.title}</h3>
                <p className="text-sm text-gray-600" style={{ lineHeight: 1.6 }}>{b.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Signup Form */}
      <div className="max-w-xl mx-auto px-6 pb-16">
        <Card className="rounded-none border-2" style={{ borderColor: '#C4A084' }}>
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl mb-2" style={{ color: '#6D5A47' }}>Jetzt anmelden</h2>
              <p className="text-sm text-gray-600">
                Sichern Sie sich Ihren <strong style={{ color: '#C4A084' }}>10% Willkommens-Rabatt</strong>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1 text-gray-700">Ihr Vorname (optional)</label>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Max"
                  className="rounded-none"
                  data-testid="newsletter-firstname-input"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-700">Ihre E-Mail-Adresse *</label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="max@beispiel.de"
                  className="rounded-none"
                  data-testid="newsletter-email-input"
                />
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1"
                  data-testid="newsletter-consent-checkbox"
                  required
                />
                <span>
                  Ich möchte den Newsletter erhalten und stimme der{' '}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#8B7355' }}>
                    Datenschutzerklärung
                  </a>{' '}
                  zu. Ich kann mich jederzeit abmelden.
                </span>
              </label>

              <Button
                type="submit"
                disabled={loading || !accepted}
                className="w-full rounded-none text-white"
                style={{ backgroundColor: '#C4A084' }}
                data-testid="newsletter-submit-btn"
              >
                {loading ? 'Wird angemeldet...' : 'Anmelden & 10% sichern'}
              </Button>

              <p className="text-xs text-gray-500 text-center" style={{ lineHeight: 1.6 }}>
                Sie erhalten eine Bestätigungs-E-Mail (Double-Opt-In gem. DSGVO).
                Ihre Daten werden ausschließlich für den Newsletter-Versand verwendet.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
